import {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";

import {
  Users,
  Search,
  Trash2,
  Phone,
  Loader2,
  RefreshCw,
  Edit3,
  Save,
  X,
  Mail,
  UserRound,
  CircleCheck,
  Clock3,
  AlertTriangle,
  ImagePlus,
  Camera,
  Plus,
  ShieldCheck,
 
  ChevronRight,
} from "lucide-react";

import API from "../../services/api";

import "../../styles/clients.css";

// =========================================================
// BACKEND / MEDIA
// =========================================================

const BACKEND_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, "")
  : "http://127.0.0.1:8000";

// =========================================================
// INITIAL FORM
// =========================================================

const INITIAL_FORM = {
  username: "",
  email: "",
  phone: "",
  bio: "",
};

// =========================================================
// COMPONENT
// =========================================================

export default function ClientsManager() {
  // =======================================================
  // STATE
  // =======================================================

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [search, setSearch] = useState("");

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // =======================================================
  // CREATE / EDIT
  // =======================================================

  const [editingId, setEditingId] = useState(null);
  const [creating, setCreating] = useState(false);

  const [form, setForm] = useState(INITIAL_FORM);

  // =======================================================
  // FOTO
  // =======================================================

  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState("");

  const fileInputRef = useRef(null);

  // =======================================================
  // HELPERS
  // =======================================================

  const clearMessages = useCallback(() => {
    setError("");
    setSuccess("");
  }, []);

  // =======================================================
  // API ERROR
  // =======================================================

  const getApiError = (err, fallback) => {
    const data = err?.response?.data;

    if (typeof data === "string") {
      return data;
    }

    if (data?.detail) {
      return data.detail;
    }

    if (data?.message) {
      return data.message;
    }

    if (data && typeof data === "object") {
      const firstError = Object.values(data)
        .flat()
        .find(Boolean);

      if (firstError) {
        return String(firstError);
      }
    }

    return fallback;
  };

  // =======================================================
  // AVATAR URL
  // =======================================================

  const getAvatar = (user) => {
    if (!user?.avatar || typeof user.avatar !== "string") {
      return null;
    }

    if (
      user.avatar.startsWith("http://") ||
      user.avatar.startsWith("https://") ||
      user.avatar.startsWith("blob:") ||
      user.avatar.startsWith("data:")
    ) {
      return user.avatar;
    }

    return `${BACKEND_URL}${
      user.avatar.startsWith("/") ? "" : "/"
    }${user.avatar}`;
  };

  // =======================================================
  // LOAD CLIENTS
  // =======================================================

  const loadClients = useCallback(async () => {
    try {
      setLoading(true);
      clearMessages();

      const { data } = await API.get("/auth/users/");

      const rows =
        data?.results ||
        data?.data ||
        data ||
        [];

      const onlyClients = Array.isArray(rows)
        ? rows.filter(
            (user) => user.role === "cliente"
          )
        : [];

      setClients(onlyClients);
    } catch (err) {
      setError(
        getApiError(
          err,
          "Error cargando usuarios."
        )
      );
    } finally {
      setLoading(false);
    }
  }, [clearMessages]);

  // =======================================================
  // INITIAL LOAD
  // =======================================================

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  // =======================================================
  // CLEAN BLOB URL
  // =======================================================

  useEffect(() => {
    return () => {
      if (
        avatarPreview &&
        avatarPreview.startsWith("blob:")
      ) {
        URL.revokeObjectURL(avatarPreview);
      }
    };
  }, [avatarPreview]);

  // =======================================================
  // HANDLE FORM
  // =======================================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // =======================================================
  // CREATE CLIENT
  // =======================================================

  const createClient = () => {
    clearMessages();

    setEditingId(null);
    setCreating(true);

    setForm({
      ...INITIAL_FORM,
    });

    setAvatarFile(null);
    setAvatarPreview("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =======================================================
  // AVATAR SELECT
  // =======================================================

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    clearMessages();

    if (!file.type.startsWith("image/")) {
      setError(
        "Selecciona una imagen válida."
      );

      e.target.value = "";
      return;
    }

    const maxSize = 5 * 1024 * 1024;

    if (file.size > maxSize) {
      setError(
        "La imagen no puede superar los 5 MB."
      );

      e.target.value = "";
      return;
    }

    const preview = URL.createObjectURL(file);

    setAvatarFile(file);
    setAvatarPreview(preview);
  };

  // =======================================================
  // REMOVE SELECTED AVATAR
  // =======================================================

  const removeSelectedAvatar = () => {
    setAvatarFile(null);

    if (creating) {
      setAvatarPreview("");
    } else {
      const currentUser = clients.find(
        (user) => user.id === editingId
      );

      setAvatarPreview(
        getAvatar(currentUser) || ""
      );
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // =======================================================
  // RESET FORM
  // =======================================================

  const resetForm = () => {
    setEditingId(null);
    setCreating(false);

    setForm({
      ...INITIAL_FORM,
    });

    setAvatarFile(null);
    setAvatarPreview("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // =======================================================
  // EDIT CLIENT
  // =======================================================

  const editClient = (user) => {
    clearMessages();

    setCreating(false);
    setEditingId(user.id);

    setForm({
      username: user.username || "",
      email: user.email || "",
      phone: user.phone || "",
      bio: user.bio || "",
    });

    setAvatarFile(null);
    setAvatarPreview(
      getAvatar(user) || ""
    );

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =======================================================
  // SAVE / CREATE CLIENT
  // =======================================================

  const saveClient = async () => {
    const isCreating = creating;
    const isEditing = Boolean(editingId);

    if (!isCreating && !isEditing) {
      setError(
        "Selecciona una operación válida."
      );
      return;
    }

    if (!form.username.trim()) {
      setError(
        "El nombre de usuario es obligatorio."
      );
      return;
    }

    if (!form.email.trim()) {
      setError(
        "El correo electrónico es obligatorio."
      );
      return;
    }

    try {
      setSaving(true);
      clearMessages();

      // ===================================================
      // FORMDATA CON FOTO
      // ===================================================

      if (avatarFile) {
        const formData = new FormData();

        formData.append(
          "username",
          form.username.trim()
        );

        formData.append(
          "email",
          form.email.trim()
        );

        formData.append(
          "phone",
          form.phone.trim()
        );

        formData.append(
          "bio",
          form.bio.trim()
        );

        if (isCreating) {
          formData.append(
            "role",
            "cliente"
          );
        }

        formData.append(
          "avatar",
          avatarFile
        );

        if (isCreating) {
          await API.post(
            "/auth/users/",
            formData,
            {
              headers: {
                "Content-Type":
                  "multipart/form-data",
              },
            }
          );
        } else {
          await API.put(
            `/auth/users/${editingId}/`,
            formData,
            {
              headers: {
                "Content-Type":
                  "multipart/form-data",
              },
            }
          );
        }
      }

      // ===================================================
      // JSON SIN FOTO
      // ===================================================

      else {
        const payload = {
          username:
            form.username.trim(),

          email:
            form.email.trim(),

          phone:
            form.phone.trim(),

          bio:
            form.bio.trim(),
        };

        if (isCreating) {
          payload.role = "cliente";

          await API.post(
            "/auth/users/",
            payload
          );
        } else {
          await API.put(
            `/auth/users/${editingId}/`,
            payload
          );
        }
      }

      // ===================================================
      // SUCCESS
      // ===================================================

      setSuccess(
        isCreating
          ? "Cliente creado correctamente."
          : "Usuario actualizado correctamente."
      );

      resetForm();

      await loadClients();
    } catch (err) {
      setError(
        getApiError(
          err,
          isCreating
            ? "Error creando cliente."
            : "Error actualizando usuario."
        )
      );
    } finally {
      setSaving(false);
    }
  };

  // =======================================================
  // DELETE CLIENT
  // =======================================================

  const deleteClient = async (id) => {
    const user = clients.find(
      (item) => item.id === id
    );

    const confirmed = window.confirm(
      `¿Estás seguro de que deseas eliminar a "${
        user?.username || "este usuario"
      }"?

Esta acción no se puede deshacer.`
    );

    if (!confirmed) {
      return;
    }

    try {
      clearMessages();

      setDeletingId(id);

      await API.delete(
        `/auth/users/${id}/`
      );

      setClients((prev) =>
        prev.filter(
          (userItem) =>
            userItem.id !== id
        )
      );

      if (editingId === id) {
        resetForm();
      }

      setSuccess(
        "Usuario eliminado correctamente."
      );
    } catch (err) {
      setError(
        getApiError(
          err,
          "Error eliminando usuario."
        )
      );
    } finally {
      setDeletingId(null);
    }
  };

  // =======================================================
  // FILTER
  // =======================================================

  const filtered = useMemo(() => {
    const term = search
      .trim()
      .toLowerCase();

    if (!term) {
      return clients;
    }

    return clients.filter((user) => {
      const searchable = `
        ${user.username || ""}
        ${user.email || ""}
        ${user.phone || ""}
        ${user.bio || ""}
        ${user.id || ""}
      `.toLowerCase();

      return searchable.includes(term);
    });
  }, [clients, search]);

  // =======================================================
  // STATS
  // =======================================================

  const verifiedCount = useMemo(
    () =>
      clients.filter(
        (user) => user.is_verified
      ).length,
    [clients]
  );

  const pendingCount = useMemo(
    () =>
      clients.filter(
        (user) => !user.is_verified
      ).length,
    [clients]
  );

  const avatarCount = useMemo(
    () =>
      clients.filter(
        (user) => Boolean(user.avatar)
      ).length,
    [clients]
  );

  // =======================================================
  // RENDER
  // =======================================================

  return (
    <div className="clients">

      {/* ===================================================
          HEADER
      =================================================== */}

      <header className="clients__header">

        <div className="clients__heading">

          <div className="clients__eyebrow">
            <span className="clients__eyebrow-line" />
            CUSTOMER MANAGEMENT
            <span className="clients__eyebrow-status">
              <span />
              ONLINE
            </span>
          </div>

          <h2>
            <span className="clients__title-icon">
              <Users size={22} />
            </span>

            Clientes
          </h2>

          <p>
            Administra usuarios, perfiles,
            fotografías y datos de contacto
            de DEBUTS CROP.
          </p>

        </div>

        <div className="clients__header-actions">

          <button
            type="button"
            className="clients__create"
            onClick={createClient}
            disabled={loading || saving}
            title="Crear nuevo cliente"
          >
            <Plus size={18} />

            <span>
              Nuevo cliente
            </span>

            <ChevronRight
              size={15}
              className="clients__create-arrow"
            />
          </button>

          <button
            type="button"
            className="clients__refresh"
            onClick={loadClients}
            disabled={loading || saving}
            title="Actualizar clientes"
          >
            {loading ? (
              <Loader2
                size={18}
                className="spin"
              />
            ) : (
              <RefreshCw size={18} />
            )}

            <span>
              Actualizar
            </span>
          </button>

        </div>

      </header>

      {/* ===================================================
          STATS
      =================================================== */}

      <section className="clients__stats">

        <article className="clients__stat-card clients__stat-card--total">

          <div className="clients__stat-grid" />

          <div className="clients__stat-icon">
            <Users size={21} />
          </div>

          <div className="clients__stat-content">
            <span>
              Total clientes
            </span>

            <strong>
              {clients.length}
            </strong>

            <small>
              Base registrada
            </small>
          </div>

          <div className="clients__stat-mark">
            01
          </div>

        </article>

        <article className="clients__stat-card clients__stat-card--verified">

          <div className="clients__stat-grid" />

          <div className="clients__stat-icon">
            <CircleCheck size={21} />
          </div>

          <div className="clients__stat-content">
            <span>
              Verificados
            </span>

            <strong>
              {verifiedCount}
            </strong>

            <small>
              Cuenta activa
            </small>
          </div>

          <div className="clients__stat-mark">
            02
          </div>

        </article>

        <article className="clients__stat-card clients__stat-card--pending">

          <div className="clients__stat-grid" />

          <div className="clients__stat-icon">
            <Clock3 size={21} />
          </div>

          <div className="clients__stat-content">
            <span>
              Pendientes
            </span>

            <strong>
              {pendingCount}
            </strong>

            <small>
              Requieren atención
            </small>
          </div>

          <div className="clients__stat-mark">
            03
          </div>

        </article>

        <article className="clients__stat-card clients__stat-card--avatar">

          <div className="clients__stat-grid" />

          <div className="clients__stat-icon">
            <Camera size={21} />
          </div>

          <div className="clients__stat-content">
            <span>
              Con fotografía
            </span>

            <strong>
              {avatarCount}
            </strong>

            <small>
              Perfil visual
            </small>
          </div>

          <div className="clients__stat-mark">
            04
          </div>

        </article>

      </section>

      {/* ===================================================
          MESSAGES
      =================================================== */}

      {error && (
        <div className="clients__message clients__message--error">

          <AlertTriangle size={18} />

          <span>
            {error}
          </span>

          <button
            type="button"
            onClick={() => setError("")}
            aria-label="Cerrar mensaje"
          >
            <X size={15} />
          </button>

        </div>
      )}

      {success && (
        <div className="clients__message clients__message--success">

          <CircleCheck size={18} />

          <span>
            {success}
          </span>

          <button
            type="button"
            onClick={() => setSuccess("")}
            aria-label="Cerrar mensaje"
          >
            <X size={15} />
          </button>

        </div>
      )}

      {/* ===================================================
          CREATE / EDITOR
      =================================================== */}

      {(editingId || creating) && (
        <section className="clients__editor">

          <div className="clients__editor-glow" />

          {/* HEADER */}

          <div className="clients__editor-header">

            <div>

              <span className="clients__section-label">
                {creating
                  ? "NEW CLIENT"
                  : "EDIT MODE"}
              </span>

              <h3>
                {creating
                  ? "Crear cliente"
                  : "Editar cliente"}
              </h3>

              <p>
                {creating
                  ? "Registra un nuevo cliente en DEBUTS CROP."
                  : "Actualiza la información, fotografía y datos de contacto del usuario seleccionado."}
              </p>

            </div>

            <button
              type="button"
              className="clients__editor-close"
              onClick={resetForm}
              disabled={saving}
              title="Cerrar"
            >
              <X size={19} />
            </button>

          </div>

          {/* PROFILE IMAGE */}

          <div className="clients__avatar-editor">

            <div className="clients__avatar-frame">

              <div className="clients__avatar-preview">

                {avatarPreview ? (
                  <img
                    src={avatarPreview}
                    alt={
                      form.username ||
                      "Vista previa del cliente"
                    }
                    onError={() =>
                      setAvatarPreview("")
                    }
                  />
                ) : (
                  <div className="clients__avatar-empty">
                    <UserRound size={34} />
                  </div>
                )}

                <div className="clients__avatar-overlay">
                  <Camera size={18} />
                </div>

              </div>

              <div className="clients__avatar-corner clients__avatar-corner--tl" />
              <div className="clients__avatar-corner clients__avatar-corner--tr" />
              <div className="clients__avatar-corner clients__avatar-corner--bl" />
              <div className="clients__avatar-corner clients__avatar-corner--br" />

            </div>

            <div className="clients__avatar-info">

              <span className="clients__section-label">
                PROFILE IMAGE
              </span>

              <h4>
                Fotografía del cliente
              </h4>

              <p>
                Sube una imagen para utilizarla
                como fotografía de perfil.
              </p>

              <div className="clients__avatar-actions">

                <label
                  htmlFor="client-avatar-upload"
                  className="clients__avatar-upload"
                >
                  <ImagePlus size={17} />

                  {avatarFile
                    ? "Cambiar fotografía"
                    : "Seleccionar fotografía"}

                  <input
                    ref={fileInputRef}
                    id="client-avatar-upload"
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    onChange={handleAvatarChange}
                    disabled={saving}
                  />
                </label>

                {avatarFile && (
                  <button
                    type="button"
                    className="clients__avatar-remove"
                    onClick={removeSelectedAvatar}
                    disabled={saving}
                  >
                    <X size={15} />
                    Quitar selección
                  </button>
                )}

              </div>

              <small>
                JPG · PNG · WEBP · GIF
                <span>•</span>
                máximo 5 MB
              </small>

            </div>

          </div>

          {/* FORM */}

          <div className="clients__editor-grid">

            {/* USERNAME */}

            <label>

              <span>
                Usuario
              </span>

              <div className="clients__input-wrap">

                <UserRound size={17} />

                <input
                  name="username"
                  placeholder="Nombre de usuario"
                  value={form.username}
                  onChange={handleChange}
                  autoComplete="username"
                  disabled={saving}
                />

              </div>

            </label>

            {/* EMAIL */}

            <label>

              <span>
                Email
              </span>

              <div className="clients__input-wrap">

                <Mail size={17} />

                <input
                  type="email"
                  name="email"
                  placeholder="Correo electrónico"
                  value={form.email}
                  onChange={handleChange}
                  autoComplete="email"
                  disabled={saving}
                />

              </div>

            </label>

            {/* PHONE */}

            <label>

              <span>
                Teléfono
              </span>

              <div className="clients__input-wrap">

                <Phone size={17} />

                <input
                  name="phone"
                  placeholder="Número telefónico"
                  value={form.phone}
                  onChange={handleChange}
                  autoComplete="tel"
                  disabled={saving}
                />

              </div>

            </label>

            {/* BIO */}

            <label className="clients__field-full">

              <span>
                Biografía
              </span>

              <textarea
                name="bio"
                placeholder="Información adicional del cliente..."
                value={form.bio}
                onChange={handleChange}
                rows={4}
                disabled={saving}
              />

            </label>

          </div>

          {/* ACTIONS */}

          <div className="clients__editor-actions">

            <button
              type="button"
              className="clients__button clients__button--secondary"
              onClick={resetForm}
              disabled={saving}
            >
              <X size={17} />
              Cancelar
            </button>

            <button
              type="button"
              className="clients__button clients__button--primary"
              onClick={saveClient}
              disabled={saving}
            >

              {saving ? (
                <Loader2
                  size={17}
                  className="spin"
                />
              ) : creating ? (
                <Plus size={17} />
              ) : (
                <Save size={17} />
              )}

              {saving
                ? creating
                  ? "Creando..."
                  : "Guardando..."
                : creating
                ? "Crear cliente"
                : "Guardar cambios"}

            </button>

          </div>

        </section>
      )}

      {/* ===================================================
          TOOLBAR
      =================================================== */}

      <section className="clients__toolbar">

        <div className="clients__search">

          <Search size={18} />

          <input
            type="search"
            placeholder="Buscar por usuario, email, teléfono, ID o bio..."
            value={search}
            onChange={(e) =>
              setSearch(e.target.value)
            }
          />

          {search && (
            <button
              type="button"
              className="clients__search-clear"
              onClick={() => setSearch("")}
              aria-label="Limpiar búsqueda"
            >
              <X size={15} />
            </button>
          )}

        </div>

        <div className="clients__results">

          <span>
            RESULTADOS
          </span>

          <strong>
            {filtered.length}
          </strong>

        </div>

      </section>

      {/* ===================================================
          CONTENT
      =================================================== */}

      {loading ? (
        <div className="clients__loading">

          <div className="clients__loading-ring">
            <Loader2
              size={38}
              className="spin"
            />
          </div>

          <strong>
            Cargando clientes
          </strong>

          <span>
            Sincronizando información...
          </span>

        </div>
      ) : filtered.length === 0 ? (
        <div className="clients__empty">

          <div className="clients__empty-grid" />

          <div className="clients__empty-icon">
            <Users size={32} />
          </div>

          <span className="clients__section-label">
            CLIENT DATABASE
          </span>

          <h3>
            {search
              ? "No encontramos clientes"
              : "No hay clientes registrados"}
          </h3>

          <p>
            {search
              ? "Prueba con otro término de búsqueda."
              : "Puedes crear el primer cliente desde el botón Nuevo cliente."}
          </p>

          {search ? (
            <button
              type="button"
              onClick={() => setSearch("")}
            >
              <X size={16} />
              Limpiar búsqueda
            </button>
          ) : (
            <button
              type="button"
              onClick={createClient}
            >
              <Plus size={17} />
              Crear cliente
            </button>
          )}

        </div>
      ) : (
        <section className="clients__table-wrap">

          {/* TABLE HEADER */}

          <div className="clients__table-top">

            <div>

              <span className="clients__section-label">
                CLIENT DATABASE
              </span>

              <h3>
                Usuarios registrados
              </h3>

            </div>

            <div className="clients__table-meta">

              <ShieldCheck size={15} />

              <div className="clients__table-count">
                {filtered.length}

                <span>
                  {filtered.length === 1
                    ? "cliente"
                    : "clientes"}
                </span>
              </div>

            </div>

          </div>

          {/* TABLE */}

          <div className="clients__table-scroll">

            <table className="clients__table">

              <thead>
                <tr>
                  <th>
                    Cliente
                  </th>

                  <th>
                    Contacto
                  </th>

                  <th>
                    Información
                  </th>

                  <th>
                    Estado
                  </th>

                  <th>
                    Acciones
                  </th>
                </tr>
              </thead>

              <tbody>

                {filtered.map((user) => {
                  const avatar =
                    getAvatar(user);

                  const isDeleting =
                    deletingId === user.id;

                  const isEditing =
                    editingId === user.id;

                  return (
                    <tr
                      key={user.id}
                      className={
                        isEditing
                          ? "is-editing"
                          : ""
                      }
                    >

                      {/* CLIENTE */}

                      <td>

                        <div className="client-user">

                          {avatar ? (
                            <img
                              src={avatar}
                              alt={
                                user.username ||
                                "Cliente"
                              }
                              className="client-avatar"
                              onError={(e) => {
                                e.currentTarget.style.display =
                                  "none";

                                if (
                                  e.currentTarget
                                    .nextElementSibling
                                ) {
                                  e.currentTarget
                                    .nextElementSibling
                                    .style.display =
                                    "flex";
                                }
                              }}
                            />
                          ) : null}

                          <div
                            className="client-avatar-fallback"
                            style={{
                              display: avatar
                                ? "none"
                                : "flex",
                            }}
                          >
                            {user.username
                              ?.charAt(0)
                              ?.toUpperCase() || (
                              <UserRound size={19} />
                            )}
                          </div>

                          <div className="client-user-info">

                            <strong>
                              {user.username ||
                                "Sin usuario"}
                            </strong>

                            <span>
                              ID #{user.id}
                            </span>

                          </div>

                        </div>

                      </td>

                      {/* CONTACTO */}

                      <td>

                        <div className="client-contact">

                          <div>
                            <Mail size={14} />

                            <span>
                              {user.email ||
                                "Sin email"}
                            </span>
                          </div>

                          <div>
                            <Phone size={14} />

                            <span>
                              {user.phone ||
                                "Sin teléfono"}
                            </span>
                          </div>

                        </div>

                      </td>

                      {/* BIO */}

                      <td>

                        <div className="client-bio">

                          {user.bio ? (
                            <span title={user.bio}>
                              {user.bio}
                            </span>
                          ) : (
                            <em>
                              Sin información
                            </em>
                          )}

                        </div>

                      </td>

                      {/* ESTADO */}

                      <td>

                        <div
                          className={
                            user.is_verified
                              ? "client-status client-status--verified"
                              : "client-status client-status--pending"
                          }
                        >

                          {user.is_verified ? (
                            <CircleCheck size={15} />
                          ) : (
                            <Clock3 size={15} />
                          )}

                          <span>
                            {user.is_verified
                              ? "Verificado"
                              : "Pendiente"}
                          </span>

                        </div>

                      </td>

                      {/* ACCIONES */}

                      <td>

                        <div className="client-actions">

                          <button
                            type="button"
                            className="client-action client-action--edit"
                            onClick={() =>
                              editClient(user)
                            }
                            disabled={
                              saving ||
                              isDeleting
                            }
                            title="Editar cliente"
                          >
                            <Edit3 size={15} />
                          </button>

                          <button
                            type="button"
                            className="client-action client-action--delete"
                            onClick={() =>
                              deleteClient(
                                user.id
                              )
                            }
                            disabled={
                              saving ||
                              isDeleting
                            }
                            title="Eliminar cliente"
                          >
                            {isDeleting ? (
                              <Loader2
                                size={15}
                                className="spin"
                              />
                            ) : (
                              <Trash2 size={15} />
                            )}
                          </button>

                        </div>

                      </td>

                    </tr>
                  );
                })}

              </tbody>

            </table>

          </div>

        </section>
      )}

    </div>
  );
}